/**
 * Derive the on-disk / GitHub-repo identifier (`game:create` → `options.name`)
 * for the **new-UI create flow**, from the CONCEPT TEXT the user typed.
 *
 * # Why this exists (#1824)
 *
 * The new-UI "+ New Game" flow has no user-typed name field — the user types a
 * concept/prompt and the game is scaffolded. Until now the slug sent as
 * `options.name` was a PURELY random codename (e.g. "frozen-crest-15"), and
 * core uses `options.name` verbatim as the directory id, package scope,
 * `crucible.json` gameId, AND GitHub repo name (`crucible-game-<id>`). So
 * every new game's folder + repo read as a random codename instead of
 * anything to do with the game. That is exactly the #1824 report
 * ("flaming-gambit-sandwich-15" instead of "Peggle").
 *
 * Product-owner design call (2026-06-30): derive the slug from the concept the
 * user typed, producing CLEAN names with a numeric `-2`/`-3` disambiguator
 * ONLY on collision — not an always-on random suffix. So
 * "a peggle-style ball bouncer" → `peggle-style-ball-bouncer`. A name that
 * collides with an existing game id gets `-2`, `-3`, … bumped on.
 *
 * Falls back to a readable random codename ONLY when the concept is empty or
 * un-sluggable (e.g. emoji-only / non-latin that strips to nothing). The
 * result is ALWAYS a valid kebab id per `validateGameName`
 * (`packages/crucible/src/template/tokens.ts`):
 * `^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$`, length 3–50.
 *
 * The legacy create UI derives its slug from the typed game NAME
 * (`slugifyToKebab(gameName)`) and is unchanged.
 */
import { slugifyToKebab } from './slugify.js';

/**
 * Validator regex from `packages/crucible/src/template/tokens.ts`. Kept in
 * sync with the slugify-test pin so a future divergence fails loudly.
 */
const VALIDATOR_REGEX = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
const MIN_KEBAB_LEN = 3;
const MAX_KEBAB_LEN = 50;
/** Cap the concept-derived base to a handful of significant words. */
const MAX_WORDS = 4;
/** …and to a readable character budget, leaving room for a `-NN` suffix. */
const MAX_BASE_LEN = 40;

/**
 * Grammatical/filler stopwords dropped from the concept before slugifying.
 * Mirrors the established set in
 * `packages/crucible/src/ingot/catalogue-resolution.ts` (function words +
 * generic game-prose words like "game"/"make"/"want") so the two stay
 * consistent. Deliberately does NOT classify content nouns (e.g. "ball",
 * "ship") as filler — that would be brittle semantic guessing
 * (see .claude/rules/regex-not-for-semantic-classification.md). It only
 * removes words that carry no signal about WHAT the game is.
 */
const STOPWORDS = new Set([
  'a',
  'an',
  'and',
  'any',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'can',
  'do',
  'each',
  'for',
  'from',
  'game',
  'games',
  'genre',
  'has',
  'have',
  'how',
  'in',
  'into',
  'is',
  'it',
  'its',
  'like',
  'make',
  'me',
  'many',
  'mode',
  'more',
  'much',
  'new',
  'no',
  'not',
  'of',
  'on',
  'one',
  'or',
  'over',
  'per',
  'should',
  'so',
  'some',
  'than',
  'that',
  'the',
  'their',
  'them',
  'then',
  'they',
  'this',
  'to',
  'up',
  'use',
  'via',
  'want',
  'wants',
  'we',
  'when',
  'where',
  'which',
  'will',
  'with',
  'write',
  'you',
  'your',
]);

const SLUG_ADJECTIVES = [
  'swift',
  'brave',
  'cosmic',
  'neon',
  'electric',
  'frozen',
  'blazing',
  'shadow',
  'golden',
  'crystal',
  'iron',
  'silver',
  'dark',
  'wild',
  'azure',
];
const SLUG_NOUNS = [
  'forge',
  'quest',
  'arena',
  'vault',
  'hawk',
  'tiger',
  'pilot',
  'drift',
  'spark',
  'realm',
  'shift',
  'ridge',
  'pulse',
  'rift',
  'crest',
];

/**
 * A readable random `adjective-noun-NN` codename. Only used as the fallback
 * when the concept can't produce a usable slug.
 */
export function generateRandomSlug(rng: () => number = Math.random): string {
  const adj = SLUG_ADJECTIVES[Math.floor(rng() * SLUG_ADJECTIVES.length)];
  const noun = SLUG_NOUNS[Math.floor(rng() * SLUG_NOUNS.length)];
  const num = Math.floor(rng() * 90) + 10; // 10–99, two digits
  return `${adj}-${noun}-${num}`;
}

/**
 * Reduce a free-text concept to a clean kebab base of a few significant words.
 *
 * - Tokenise on any non-`[a-z0-9]` run (so "peggle-style" → "peggle","style").
 * - Drop grammatical/filler stopwords (articles, prepositions, "game", …).
 * - Keep the FIRST {@link MAX_WORDS} significant words, in order.
 * - Join with `-`, then enforce the {@link MAX_BASE_LEN} char budget on a
 *   word boundary, and strip stray edge dashes.
 *
 * Returns `''` when the concept yields no usable base (empty / stopwords-only /
 * emoji-only). The caller falls back to a random codename for that case.
 *
 * Example: "a peggle-style ball bouncer" → "peggle-style-ball-bouncer".
 */
export function conceptToSlugBase(conceptText: string | undefined): string {
  if (!conceptText) return '';
  const words = slugifyToKebab(conceptText)
    .split('-')
    // Drop single-character tokens ("i", "a", stray letters from punctuation)
    // — they carry no naming signal — plus the grammatical/filler stopwords.
    .filter((w) => w.length > 1 && !STOPWORDS.has(w));
  if (words.length === 0) return '';
  let base = words.slice(0, MAX_WORDS).join('-');
  if (base.length > MAX_BASE_LEN) {
    // Trim on a word boundary so we don't cut a word mid-token.
    base = base.slice(0, MAX_BASE_LEN).replace(/-[^-]*$/, '');
  }
  return base.replace(/^-+|-+$/g, '');
}

/**
 * Disambiguate a base slug against a set of already-taken game ids by
 * appending the SMALLEST numeric suffix (`-2`, `-3`, …) that's free. Returns
 * the base unchanged when it isn't taken. Suffix digits are reserved within
 * the 50-char validator ceiling.
 */
function disambiguate(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const suffix = `-${n}`;
    const trimmed = base.slice(0, MAX_KEBAB_LEN - suffix.length).replace(/-+$/g, '');
    const candidate = `${trimmed}${suffix}`;
    if (!taken.has(candidate) && VALIDATOR_REGEX.test(candidate)) return candidate;
  }
  // Pathological (1000 same-named games): fall through to base; the
  // CRUCIBLE-201 pre-flight + recovery backstops a true collision.
  return base;
}

/**
 * Build the create slug from the user-typed concept text.
 *
 * 1. Derive a clean kebab base from the concept ({@link conceptToSlugBase}).
 * 2. If the base is a valid kebab id, disambiguate it against `existingGameIds`
 *    with a `-2`/`-3` suffix ONLY when it collides (no suffix in the common
 *    case). A true race / stale-list collision is backstopped by the existing
 *    CRUCIBLE-201 pre-flight repo-exists check + recovery branch in core.
 * 3. If the concept can't produce a usable base (empty / un-sluggable), fall
 *    back to a readable random codename (also disambiguated for safety).
 *
 * The result is always a validator-legal kebab id (length 3–50).
 *
 * @param conceptText the prompt the user typed in the new-UI create flow.
 * @param existingGameIds ids already in use (best-effort, from `game:list`);
 *   pass an empty set when no list is available — collisions then rely on the
 *   core pre-flight backstop.
 * @param rng injectable RNG for deterministic tests; defaults to `Math.random`.
 */
export function deriveCreateNameFromConcept(
  conceptText: string | undefined,
  existingGameIds: Iterable<string> = [],
  rng: () => number = Math.random,
): string {
  const taken = existingGameIds instanceof Set ? existingGameIds : new Set(existingGameIds);
  const base = conceptToSlugBase(conceptText);
  if (base.length >= MIN_KEBAB_LEN && VALIDATOR_REGEX.test(base)) {
    return disambiguate(base, taken);
  }
  // Un-sluggable concept → readable random codename (disambiguated too, so a
  // retry that re-rolls the same codename doesn't collide).
  return disambiguate(generateRandomSlug(rng), taken);
}
